import Phaser from 'phaser';
import level1Json from '@/Levels/JSON/level1.json';
import { LevelDataStructure } from '../Scripts/Systems/LevelManager';
import { DiscordRichPresenceManager } from '../Scripts/Systems/DiscordRichPresenceManager';

export interface EditorTool {
  id: string;
  name: string;
  category: 'platforms' | 'hazards' | 'interactive' | 'portals' | 'enemies' | 'collectibles' | 'special' | 'eraser';
  type: string;
  icon: string;
}

export class LevelEditorScene extends Phaser.Scene {
  private levelData!: LevelDataStructure;
  private selectedTool: EditorTool = { id: 'tile_ground', name: 'Ground Tile', category: 'platforms', type: 'tile_ground', icon: '🧱' };
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private previewSprite!: Phaser.GameObjects.Sprite;
  private objectContainer!: Phaser.GameObjects.Container;
  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private camStartX = 0;
  private camStartY = 0;
  private uiOverlay!: HTMLDivElement;

  constructor() {
    super('LevelEditorScene');
  }

  public init(data?: { level?: LevelDataStructure }): void {
    if (data && data.level) {
      this.levelData = JSON.parse(JSON.stringify(data.level));
    } else {
      this.levelData = JSON.parse(JSON.stringify(level1Json));
    }
  }

  public create(): void {
    DiscordRichPresenceManager.updatePresence('Designing Game Level', 'In Developer Level Editor');

    // 1. World Bounds
    this.cameras.main.setBounds(0, 0, this.levelData.width, this.levelData.height);
    this.cameras.main.setScroll(this.levelData.spawn.x - 200, this.levelData.spawn.y - 300);

    // 2. Parallax Background
    this.add.graphics()
      .fillGradientStyle(0x0f172a, 0x1e1b4b, 0x311b92, 0x4a148c, 1)
      .fillRect(0, 0, this.levelData.width, this.levelData.height);

    // 3. Grid Graphics & Object Container
    this.gridGraphics = this.add.graphics();
    this.objectContainer = this.add.container(0, 0);

    // 4. Ghost Preview Sprite
    this.previewSprite = this.add.sprite(0, 0, 'tile_ground').setAlpha(0.6).setDepth(100);

    // 5. Draw Level & Grid
    this.renderLevelObjects();
    this.drawGrid();

    // 6. Camera Panning & Mouse Listeners
    this.setupMouseEvents();

    // 7. Mount Developer UI Overlay
    this.createDOMOverlay();

    // Clean up DOM on scene shutdown
    this.events.once('shutdown', () => {
      if (this.uiOverlay && this.uiOverlay.parentNode) {
        this.uiOverlay.parentNode.removeChild(this.uiOverlay);
      }
    });
  }

  private drawGrid(): void {
    this.gridGraphics.clear();
    this.gridGraphics.lineStyle(1, 0x334155, 0.4);

    const step = this.levelData.tileSize || 32;
    for (let x = 0; x <= this.levelData.width; x += step) {
      this.gridGraphics.lineBetween(x, 0, x, this.levelData.height);
    }
    for (let y = 0; y <= this.levelData.height; y += step) {
      this.gridGraphics.lineBetween(0, y, this.levelData.width, y);
    }
  }

  private renderLevelObjects(): void {
    this.objectContainer.removeAll(true);

    // Render Platforms
    this.levelData.platforms.forEach((p, index) => {
      for (let x = p.x; x < p.x + p.width; x += 32) {
        for (let y = p.y; y < p.y + p.height; y += 32) {
          const tile = this.add.sprite(x + 16, y + 16, p.type);
          tile.setData('meta', { category: 'platforms', index });
          this.objectContainer.add(tile);
        }
      }
    });

    // Render Moving Platforms
    this.levelData.movingPlatforms.forEach((mp, index) => {
      const tile = this.add.sprite(mp.x + 16, mp.y + 16, 'tile_wood').setTint(0x38bdf8);
      tile.setData('meta', { category: 'movingPlatforms', index });
      this.objectContainer.add(tile);
    });

    // Render Hazards
    this.levelData.hazards.forEach((h, index) => {
      const key = h.type === 'lava' ? 'tile_lava' : 'tile_breakable';
      const hazard = this.add.sprite(h.x + h.width / 2, h.y + h.height / 2, key).setTint(h.type === 'lava' ? 0xff4444 : 0xaa2222);
      hazard.setData('meta', { category: 'hazards', index });
      this.objectContainer.add(hazard);
    });

    // Render Bounce Flowers
    this.levelData.interactiveObjects.bounceFlowers.forEach((bf, index) => {
      const flower = this.add.sprite(bf.x + 16, bf.y + 16, 'bounce_flower');
      flower.setData('meta', { category: 'bounceFlowers', index });
      this.objectContainer.add(flower);
    });

    // Render Vines
    this.levelData.interactiveObjects.vines.forEach((v, index) => {
      for (let i = 0; i < v.length; i++) {
        const vine = this.add.sprite(v.x + 16, v.y + i * 32 + 16, 'vine');
        vine.setData('meta', { category: 'vines', index });
        this.objectContainer.add(vine);
      }
    });

    // Render Switches & Doors
    this.levelData.interactiveObjects.switches.forEach((sw, index) => {
      const sSprite = this.add.sprite(sw.x + 16, sw.y + 16, 'pressure_switch');
      sSprite.setData('meta', { category: 'switches', index });
      this.objectContainer.add(sSprite);
    });

    this.levelData.interactiveObjects.doors.forEach((d, index) => {
      const dSprite = this.add.sprite(d.x + 16, d.y + 16, 'door_locked');
      dSprite.setData('meta', { category: 'doors', index });
      this.objectContainer.add(dSprite);
    });

    // Render Portals
    this.levelData.portals.forEach((p, index) => {
      const portal = this.add.sprite(p.x + 16, p.y + 16, p.type);
      portal.setData('meta', { category: 'portals', index });
      this.objectContainer.add(portal);
    });

    // Render Checkpoints
    this.levelData.checkpoints.forEach((cp, index) => {
      const flag = this.add.sprite(cp.x + 16, cp.y + 16, 'checkpoint_flag');
      flag.setData('meta', { category: 'checkpoints', index });
      this.objectContainer.add(flag);
    });

    // Render Enemies
    this.levelData.enemies.forEach((e, index) => {
      const key = e.type === 'flying' ? 'enemy_bat' : e.type === 'drone' ? 'enemy_drone' : e.type === 'golem' ? 'enemy_golem' : 'enemy_ground';
      const enemy = this.add.sprite(e.x + 16, e.y + 16, key);
      enemy.setData('meta', { category: 'enemies', index });
      this.objectContainer.add(enemy);
    });

    // Render Collectibles
    this.levelData.collectibles.coins.forEach((c, index) => {
      const coin = this.add.sprite(c.x + 16, c.y + 16, 'coin');
      coin.setData('meta', { category: 'coins', index });
      this.objectContainer.add(coin);
    });

    this.levelData.collectibles.gems.forEach((g, index) => {
      const gem = this.add.sprite(g.x + 16, g.y + 16, 'gem');
      gem.setData('meta', { category: 'gems', index });
      this.objectContainer.add(gem);
    });

    // Render Spawn & Castle
    const spawnSprite = this.add.sprite(this.levelData.spawn.x + 16, this.levelData.spawn.y + 16, 'player_idle').setTint(0x4ade80);
    spawnSprite.setData('meta', { category: 'spawn', index: 0 });
    this.objectContainer.add(spawnSprite);

    const castleSprite = this.add.sprite(this.levelData.castle.x + 32, this.levelData.castle.y + 32, 'castle_portal');
    castleSprite.setData('meta', { category: 'castle', index: 0 });
    this.objectContainer.add(castleSprite);
  }

  private setupMouseEvents(): void {
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      const worldPoint = pointer.positionToCamera(this.cameras.main) as Phaser.Math.Vector2;
      const snapX = Math.floor(worldPoint.x / 32) * 32;
      const snapY = Math.floor(worldPoint.y / 32) * 32;

      this.previewSprite.setPosition(snapX + 16, snapY + 16);

      if (this.isPanning) {
        const cam = this.cameras.main;
        cam.scrollX = this.camStartX - (pointer.x - this.panStartX) / cam.zoom;
        cam.scrollY = this.camStartY - (pointer.y - this.panStartY) / cam.zoom;
      }
    });

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown() || pointer.middleButtonDown()) {
        this.isPanning = true;
        this.panStartX = pointer.x;
        this.panStartY = pointer.y;
        this.camStartX = this.cameras.main.scrollX;
        this.camStartY = this.cameras.main.scrollY;
        return;
      }

      if (pointer.leftButtonDown()) {
        const worldPoint = pointer.positionToCamera(this.cameras.main) as Phaser.Math.Vector2;
        const snapX = Math.floor(worldPoint.x / 32) * 32;
        const snapY = Math.floor(worldPoint.y / 32) * 32;

        if (this.selectedTool.category === 'eraser') {
          this.eraseObjectAt(snapX, snapY);
        } else {
          this.placeObjectAt(snapX, snapY);
        }
      }
    });

    this.input.on('pointerup', () => {
      this.isPanning = false;
    });

    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: any, _deltaX: number, deltaY: number) => {
      const cam = this.cameras.main;
      const newZoom = Phaser.Math.Clamp(cam.zoom - deltaY * 0.001, 0.4, 2.0);
      cam.setZoom(newZoom);
    });
  }

  private placeObjectAt(x: number, y: number): void {
    const tool = this.selectedTool;

    if (tool.category === 'platforms') {
      this.levelData.platforms.push({ x, y, width: 32, height: 32, type: tool.type });
    } else if (tool.type === 'moving_platform') {
      this.levelData.movingPlatforms.push({ x, y, targetX: x + 120, targetY: y, speed: 100 });
    } else if (tool.category === 'hazards') {
      this.levelData.hazards.push({ x, y, width: 32, height: 32, type: tool.type });
    } else if (tool.type === 'bounce_flower') {
      this.levelData.interactiveObjects.bounceFlowers.push({ x, y, boostPower: 680 });
    } else if (tool.type === 'vine') {
      this.levelData.interactiveObjects.vines.push({ x, y, length: 4 });
    } else if (tool.type === 'switch') {
      const id = 'sw_' + Date.now();
      const doorId = 'door_' + Date.now();
      this.levelData.interactiveObjects.switches.push({ id, x, y, doorId });
      this.levelData.interactiveObjects.doors.push({ id: doorId, x: x + 96, y });
    } else if (tool.type === 'portal_blue' || tool.type === 'portal_purple') {
      const id = 'p_' + Date.now();
      this.levelData.portals.push({ id, targetId: id, x, y, type: tool.type as any });
    } else if (tool.type === 'checkpoint') {
      this.levelData.checkpoints.push({ id: 'cp_' + Date.now(), x, y });
    } else if (tool.category === 'enemies') {
      this.levelData.enemies.push({ type: tool.type as any, x, y, patrolDistance: 150 });
    } else if (tool.type === 'coin') {
      this.levelData.collectibles.coins.push({ x, y });
    } else if (tool.type === 'gem') {
      this.levelData.collectibles.gems.push({ x, y });
    } else if (tool.type === 'spawn') {
      this.levelData.spawn = { x, y };
    } else if (tool.type === 'castle') {
      this.levelData.castle = { x, y };
    }

    this.renderLevelObjects();
  }

  private eraseObjectAt(x: number, y: number): void {
    this.levelData.platforms = this.levelData.platforms.filter((p) => !(x >= p.x && x < p.x + p.width && y >= p.y && y < p.y + p.height));
    this.levelData.hazards = this.levelData.hazards.filter((h) => !(x >= h.x && x < h.x + h.width && y >= h.y && y < h.y + h.height));
    this.levelData.movingPlatforms = this.levelData.movingPlatforms.filter((mp) => !(mp.x === x && mp.y === y));
    this.levelData.interactiveObjects.bounceFlowers = this.levelData.interactiveObjects.bounceFlowers.filter((bf) => !(bf.x === x && bf.y === y));
    this.levelData.interactiveObjects.vines = this.levelData.interactiveObjects.vines.filter((v) => !(v.x === x && v.y === y));
    this.levelData.interactiveObjects.switches = this.levelData.interactiveObjects.switches.filter((sw) => !(sw.x === x && sw.y === y));
    this.levelData.interactiveObjects.doors = this.levelData.interactiveObjects.doors.filter((d) => !(d.x === x && d.y === y));
    this.levelData.portals = this.levelData.portals.filter((p) => !(p.x === x && p.y === y));
    this.levelData.checkpoints = this.levelData.checkpoints.filter((cp) => !(cp.x === x && cp.y === y));
    this.levelData.enemies = this.levelData.enemies.filter((e) => !(e.x === x && e.y === y));
    this.levelData.collectibles.coins = this.levelData.collectibles.coins.filter((c) => !(c.x === x && c.y === y));
    this.levelData.collectibles.gems = this.levelData.collectibles.gems.filter((g) => !(g.x === x && g.y === y));

    this.renderLevelObjects();
  }

  private createDOMOverlay(): void {
    const parent = document.getElementById('game-container') || document.body;
    this.uiOverlay = document.createElement('div');
    this.uiOverlay.style.cssText = `
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      pointer-events: none;
      z-index: 1000;
      font-family: 'Outfit', sans-serif;
    `;

    this.uiOverlay.innerHTML = `
      <!-- Top Action Toolbar -->
      <div style="position: absolute; top: 12px; left: 12px; right: 12px; display: flex; justify-content: space-between; align-items: center; background: rgba(9, 10, 16, 0.9); border: 2px solid #3b82f6; border-radius: 8px; padding: 10px 16px; pointer-events: auto; backdrop-filter: blur(8px);">
        <div style="display: flex; align-items: center; gap: 12px;">
          <span style="font-family: 'Press Start 2P', monospace; font-size: 14px; color: #facc15;">🛠️ DEV EDITOR</span>
          <input type="text" id="levelNameInput" value="${this.levelData.name}" style="background: #1e293b; border: 1px solid #475569; color: #38bdf8; font-weight: 600; padding: 6px 12px; border-radius: 6px; font-size: 14px; width: 180px;" />
        </div>

        <div style="display: flex; gap: 8px;">
          <button id="btnPlaytest" style="background: #22c55e; color: #052e16; font-weight: 700; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">▶ PLAYTEST</button>
          <button id="btnExport" style="background: #3b82f6; color: #ffffff; font-weight: 700; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">📥 EXPORT JSON</button>
          <button id="btnImport" style="background: #8b5cf6; color: #ffffff; font-weight: 700; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">📂 IMPORT JSON</button>
          <button id="btnExit" style="background: #ef4444; color: #ffffff; font-weight: 700; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">✖ MAIN MENU</button>
        </div>
      </div>

      <!-- Left Palette Sidebar -->
      <div style="position: absolute; top: 70px; left: 12px; bottom: 12px; width: 220px; background: rgba(9, 10, 16, 0.9); border: 2px solid #2e3248; border-radius: 8px; padding: 12px; pointer-events: auto; overflow-y: auto; backdrop-filter: blur(8px);">
        <div style="font-weight: 700; font-size: 13px; color: #94a3b8; margin-bottom: 8px; text-transform: uppercase;">Palette Items</div>
        <div id="paletteList" style="display: flex; flex-direction: column; gap: 6px;"></div>
      </div>
    `;

    parent.appendChild(this.uiOverlay);

    this.populatePalette();
    this.bindDOMEvents();
  }

  private populatePalette(): void {
    const tools: EditorTool[] = [
      { id: 'tile_ground', name: 'Ground Tile', category: 'platforms', type: 'tile_ground', icon: '🧱' },
      { id: 'tile_stone', name: 'Stone Tile', category: 'platforms', type: 'tile_stone', icon: '🪨' },
      { id: 'tile_wood', name: 'Wooden Plank', category: 'platforms', type: 'tile_wood', icon: '🪵' },
      { id: 'tile_crystal', name: 'Crystal Ledge', category: 'platforms', type: 'tile_crystal', icon: '💎' },
      { id: 'tile_breakable', name: 'Crumbled Tile', category: 'platforms', type: 'tile_breakable', icon: '💥' },
      { id: 'tile_ice', name: 'Ice Tile', category: 'platforms', type: 'tile_ice', icon: '🧊' },
      { id: 'moving_platform', name: 'Moving Platform', category: 'special', type: 'moving_platform', icon: '🎢' },
      { id: 'spikes', name: 'Spikes', category: 'hazards', type: 'spikes', icon: '🗡️' },
      { id: 'lava', name: 'Lava Pit', category: 'hazards', type: 'lava', icon: '🌋' },
      { id: 'water', name: 'Water Basin', category: 'hazards', type: 'water', icon: '🌊' },
      { id: 'bounce_flower', name: 'Bounce Flower', category: 'interactive', type: 'bounce_flower', icon: '🌺' },
      { id: 'vine', name: 'Climb Vine', category: 'interactive', type: 'vine', icon: '🌿' },
      { id: 'switch', name: 'Switch & Door', category: 'interactive', type: 'switch', icon: '🔘' },
      { id: 'portal_blue', name: 'Blue Portal', category: 'portals', type: 'portal_blue', icon: '🌀' },
      { id: 'portal_purple', name: 'Purple Secret Portal', category: 'portals', type: 'portal_purple', icon: '🔮' },
      { id: 'checkpoint', name: 'Checkpoint Flag', category: 'special', type: 'checkpoint', icon: '🚩' },
      { id: 'enemy_ground', name: 'Ground Enemy', category: 'enemies', type: 'ground', icon: '👾' },
      { id: 'enemy_bat', name: 'Flying Bat', category: 'enemies', type: 'flying', icon: '🦇' },
      { id: 'enemy_drone', name: 'Patrol Drone', category: 'enemies', type: 'drone', icon: '🛸' },
      { id: 'enemy_golem', name: 'Ancient Golem Boss', category: 'enemies', type: 'golem', icon: '🗿' },
      { id: 'coin', name: 'Gold Coin', category: 'collectibles', type: 'coin', icon: '🪙' },
      { id: 'gem', name: 'Magic Gem', category: 'collectibles', type: 'gem', icon: '💎' },
      { id: 'spawn', name: 'Player Spawn', category: 'special', type: 'spawn', icon: '🏃' },
      { id: 'castle', name: 'Castle Goal', category: 'special', type: 'castle', icon: '🏰' },
      { id: 'eraser', name: 'Eraser Tool', category: 'eraser', type: 'eraser', icon: '🧹' },
    ];

    const list = this.uiOverlay.querySelector('#paletteList') as HTMLElement;
    if (!list) return;

    tools.forEach((tool) => {
      const btn = document.createElement('button');
      btn.style.cssText = `
        display: flex; align-items: center; gap: 8px;
        background: ${this.selectedTool.id === tool.id ? '#3b82f6' : '#1e293b'};
        color: ${this.selectedTool.id === tool.id ? '#ffffff' : '#cbd5e1'};
        border: 1px solid #334155; padding: 8px 10px; border-radius: 6px;
        cursor: pointer; font-size: 13px; text-align: left; transition: all 0.15s;
      `;
      btn.innerHTML = `<span>${tool.icon}</span> <span>${tool.name}</span>`;

      btn.addEventListener('click', () => {
        this.selectedTool = tool;
        this.updatePreviewSpriteKey();
        list.querySelectorAll('button').forEach((b) => {
          (b as HTMLElement).style.background = '#1e293b';
          (b as HTMLElement).style.color = '#cbd5e1';
        });
        btn.style.background = '#3b82f6';
        btn.style.color = '#ffffff';
      });

      list.appendChild(btn);
    });
  }

  private updatePreviewSpriteKey(): void {
    const keyMap: Record<string, string> = {
      tile_ground: 'tile_ground',
      tile_stone: 'tile_stone',
      tile_wood: 'tile_wood',
      tile_crystal: 'tile_crystal',
      tile_breakable: 'tile_breakable',
      tile_ice: 'tile_ice',
      bounce_flower: 'bounce_flower',
      vine: 'vine',
      switch: 'pressure_switch',
      portal_blue: 'portal_blue',
      portal_purple: 'portal_purple',
      checkpoint: 'checkpoint_flag',
      ground: 'enemy_ground',
      flying: 'enemy_bat',
      drone: 'enemy_drone',
      golem: 'enemy_golem',
      coin: 'coin',
      gem: 'gem',
      spawn: 'player_idle',
      castle: 'castle_portal',
    };

    const textureKey = keyMap[this.selectedTool.type] || 'tile_ground';
    this.previewSprite.setTexture(textureKey);
    this.previewSprite.setVisible(this.selectedTool.category !== 'eraser');
  }

  private bindDOMEvents(): void {
    const nameInput = this.uiOverlay.querySelector('#levelNameInput') as HTMLInputElement;
    if (nameInput) {
      nameInput.addEventListener('change', () => {
        this.levelData.name = nameInput.value || 'Custom Level';
      });
    }

    const btnPlaytest = this.uiOverlay.querySelector('#btnPlaytest');
    if (btnPlaytest) {
      btnPlaytest.addEventListener('click', () => {
        this.scene.start('GameScene', { customLevel: this.levelData });
      });
    }

    const btnExport = this.uiOverlay.querySelector('#btnExport');
    if (btnExport) {
      btnExport.addEventListener('click', () => {
        const jsonStr = JSON.stringify(this.levelData, null, 2);
        navigator.clipboard.writeText(jsonStr).then(() => {
          alert('✅ Level JSON copied to clipboard and ready to save!');
        }).catch(() => {
          prompt('Copy level JSON:', jsonStr);
        });

        // Trigger Download
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.levelData.name.toLowerCase().replace(/\s+/g, '_')}.json`;
        a.click();
        URL.revokeObjectURL(url);
      });
    }

    const btnImport = this.uiOverlay.querySelector('#btnImport');
    if (btnImport) {
      btnImport.addEventListener('click', () => {
        const jsonStr = prompt('Paste level JSON configuration here:');
        if (jsonStr) {
          try {
            const parsed = JSON.parse(jsonStr);
            this.levelData = parsed;
            this.renderLevelObjects();
            this.drawGrid();
            alert('✅ Custom level loaded successfully!');
          } catch (e) {
            alert('❌ Invalid JSON format.');
          }
        }
      });
    }

    const btnExit = this.uiOverlay.querySelector('#btnExit');
    if (btnExit) {
      btnExit.addEventListener('click', () => {
        this.scene.start('MainMenuScene');
      });
    }
  }

  public update(): void {
    const cursors = this.input.keyboard?.createCursorKeys();
    if (cursors) {
      const speed = 12 / this.cameras.main.zoom;
      if (cursors.left.isDown) this.cameras.main.scrollX -= speed;
      if (cursors.right.isDown) this.cameras.main.scrollX += speed;
      if (cursors.up.isDown) this.cameras.main.scrollY -= speed;
      if (cursors.down.isDown) this.cameras.main.scrollY += speed;
    }
  }
}
